// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.101Z
// Collection: userCredits/$walletAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Onchain credit ledger for agent creation. Doc id = wallet address. Each wallet's spendable credit balance. Balance is mutated ONLY by the onchain hooks of agentCreations (deduction) and creditTopups (grant). A missing record is treated as NEW_WALLET_CREDITS (50) by the deduction/top-up logic. Onchain so that onchain hooks in agentCreations/creditTopups can read (get) and mutate (updateField) this balance atomically — onchain hooks cannot touch offchain collections.
 */

export interface UserCreditsRequest {
  address: AddressType;
  balance: number | TimeOperation | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UserCreditsResponse {
  address: string;
  balance: number;
  createdAt: number;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a UserCredits operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUserCredits(walletAddress: string, data: UserCreditsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userCredits/${walletAddress}`, document: doc };
}

/**
 * A wallet may self-create ONLY its own record (doc id $walletAddress must equal @user.address AND @newData.address must equal @user.address), and it MUST be initialized to exactly NEW_WALLET_CREDITS (50). No other initial balance is allowed. balance/createdAt/updatedAt are required UInt (never optional) to avoid null arithmetic; frontend passes 50 and unix-seconds timestamps. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUserCredits(walletAddress: string, data: UserCreditsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userCredits/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UserCredits: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
   (Get Single Item)
 */
export async function getUserCredits(walletAddress: string): Promise<UserCreditsResponse | null> {
  try {
    const result = await get(`userCredits/${walletAddress}`);
    return result as UserCreditsResponse | null;
  } catch (error) {
    console.error(`Error getting UserCredits: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UserCredits document. (
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
  )
 */
export function subscribeUserCredits(callback: (data: UserCreditsResponse | null) => void, walletAddress: string): Promise<() => Promise<void>> {
  return subscribe(`userCredits/${walletAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UserCreditsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UserCredits at path '${`userCredits/${walletAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UserCredits items from collection userCredits
 
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
  
 */
export async function getManyUserCredits(filter: string = ""): Promise<UserCreditsResponse[]> {
  try {
    const results = await get("userCredits", { prompt: filter });
    return Array.isArray(results) ? results as UserCreditsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserCredits collection at path 'userCredits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserCredits collection at userCredits
 
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
  
 */
export function subscribeManyUserCredits(callback: (data: UserCreditsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userCredits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserCreditsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserCredits collection at path '${"userCredits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UserCredits items from collection userCredits
 
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
  
 */
export async function getAllUserCredits(filter: string = ""): Promise<UserCreditsResponse[]> {
  try {
    const results = await get("userCredits", { prompt: filter });
    return Array.isArray(results) ? results as UserCreditsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserCredits collection at path 'userCredits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserCredits collection at userCredits
 
  Read Operation Details: Public. Anyone can read any wallet's credit balance (address, balance, createdAt, updatedAt). Required for onchain collections and lets the UI display remaining credits.
  
 */
export function subscribeAllUserCredits(callback: (data: UserCreditsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userCredits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserCreditsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserCredits collection at path '${"userCredits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count UserCredits items in collection userCredits.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUserCredits(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("userCredits", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting UserCredits at path 'userCredits': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on UserCredits items in collection userCredits.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUserCredits(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("userCredits", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating UserCredits at path 'userCredits': ${formatError(error)}`);
    return { value: 0 };
  }
}

